import { toCanvas } from 'html-to-image';
import html2canvas from 'html2canvas';
import { px } from '@/lib/utils';
import { jsPDF } from 'jspdf';

export type ArtifactExportFormat = 'png' | 'pdf' | 'html';
type RasterImageMimeType = 'image/png' | 'image/webp';

export interface ArtifactExportPayload {
  format: ArtifactExportFormat;
  mimeType: string;
  base64: string;
}

export interface ExportSurfaceSnapshotOptions {
  imageMimeType?: RasterImageMimeType;
}

export type CaptureTargetWaitResult =
  | { ok: true; target: HTMLElement }
  | { ok: false; error: string };

const DEFAULT_EXPORT_TIMEOUT_MS = 25000;
const IMAGE_READY_TIMEOUT_MS = 5000;

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function textToBase64(text: string): string {
  return uint8ToBase64(new TextEncoder().encode(text));
}

function encodeCanvasDataUrl(
  canvas: HTMLCanvasElement,
  mimeType: RasterImageMimeType = 'image/png',
): string {
  if (mimeType === 'image/webp') {
    const dataUrl = canvas.toDataURL('image/webp', 1);
    // Some browsers silently fall back to PNG when WebP encoding is unavailable.
    if (dataUrl.startsWith('data:image/webp')) return dataUrl;
  }
  return canvas.toDataURL('image/png');
}

function dataUrlMimeType(dataUrl: string): string {
  const match = /^data:([^;,]+)[;,]/.exec(dataUrl);
  return match?.[1] ?? 'application/octet-stream';
}

function escapeHtml(raw: string): string {
  return raw
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getSurfaceSize(surface: HTMLElement): { width: number; height: number } {
  const rect = surface.getBoundingClientRect();
  const width = Math.max(
    1,
    Math.round(Math.max(rect.width, surface.scrollWidth, surface.clientWidth)),
  );
  const height = Math.max(
    1,
    Math.round(Math.max(rect.height, surface.scrollHeight, surface.clientHeight)),
  );
  return { width, height };
}

const DEFAULT_CANVAS_LIMIT = 8192;
const EXPORT_PIXEL_RATIO = 2;
const IFRAME_READY_TIMEOUT_MS = 5000;

function getCanvasSquareLimit(): number {
  return DEFAULT_CANVAS_LIMIT;
}

function pickSafeOutputPixelRatio(width: number, height: number): number {
  const limit = getCanvasSquareLimit();
  const edgeLimit = Math.min(limit / width, limit / height);
  const areaLimit = Math.sqrt((limit * limit) / (width * height));
  return Math.max(0.1, Math.min(EXPORT_PIXEL_RATIO, edgeLimit, areaLimit));
}

function pickSafeTilePixelRatio(width: number): number {
  const edgeLimit = getCanvasSquareLimit() / width;
  return Math.max(0.1, Math.min(EXPORT_PIXEL_RATIO, edgeLimit));
}

function pickTileHeight(width: number, pixelRatio: number): number {
  const widthPx = Math.max(1, Math.round(width * pixelRatio));
  const limit = getCanvasSquareLimit();
  const maxByEdge = Math.floor(limit / pixelRatio);
  const maxByArea = Math.floor((limit * limit) / widthPx);
  return Math.max(1, Math.min(maxByEdge, maxByArea));
}

function isTransparentColor(color: string | null | undefined): boolean {
  if (!color) return true;
  const normalized = color.trim().toLowerCase();
  if (!normalized || normalized === 'transparent') return true;
  const rgbaTransparent = /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0(?:\.0+)?\s*\)$/;
  return rgbaTransparent.test(normalized);
}

function resolveCaptureBackgroundColor(surface: HTMLElement): string {
  const fromVar = getComputedStyle(surface).getPropertyValue('--artifact-background').trim();
  if (!isTransparentColor(fromVar)) return normalizeModernColorValue(fromVar);

  let el: HTMLElement | null = surface;
  while (el) {
    const bg = getComputedStyle(el).backgroundColor;
    if (!isTransparentColor(bg)) return normalizeModernColorValue(bg);
    el = el.parentElement;
  }

  const bodyBg = getComputedStyle(document.body).backgroundColor;
  if (!isTransparentColor(bodyBg)) return normalizeModernColorValue(bodyBg);

  return '#ffffff';
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function withExportTimeout<T>(
  operation: Promise<T>,
  timeoutMs: number | undefined,
  label: string,
): Promise<T> {
  const effectiveTimeoutMs = Number.isFinite(timeoutMs)
    ? Math.max(100, Math.floor(timeoutMs as number))
    : DEFAULT_EXPORT_TIMEOUT_MS;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.ceil(effectiveTimeoutMs / 1000)}s`));
    }, effectiveTimeoutMs);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function waitForAnimationFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function waitForNextFrame(): Promise<void> {
  if (typeof requestAnimationFrame === 'function') {
    await waitForAnimationFrame();
  } else {
    await wait(16);
  }
}

function imageSource(img: HTMLImageElement): string {
  return img.currentSrc || img.src || img.getAttribute('src') || '';
}

function hasInlineImageSources(surface: HTMLElement): boolean {
  return Array.from(surface.querySelectorAll('img')).some((node) => {
    const img = node as HTMLImageElement;
    return imageSource(img).trim().toLowerCase().startsWith('data:image/');
  });
}

function waitForImageElement(img: HTMLImageElement): Promise<void> {
  const src = imageSource(img);
  if (!src || img.complete) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      img.removeEventListener('load', finish);
      img.removeEventListener('error', finish);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, IMAGE_READY_TIMEOUT_MS);
    img.addEventListener('load', finish, { once: true });
    img.addEventListener('error', finish, { once: true });
    if (img.complete) finish();
  });
}

async function waitForImagesReady(surface: HTMLElement): Promise<void> {
  const images = Array.from(surface.querySelectorAll('img')) as HTMLImageElement[];
  if (images.length === 0) return;

  await Promise.all(images.map(async (img) => {
    await waitForImageElement(img);
    if (typeof img.decode === 'function' && imageSource(img)) {
      await img.decode().catch(() => undefined);
    }
  }));
}

function isSrcdocIframeReady(iframe: HTMLIFrameElement): boolean {
  try {
    const doc = iframe.contentDocument;
    if (!doc?.documentElement || !doc.body) return false;
    const rect = iframe.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    if (doc.readyState !== 'complete') return false;
    return doc.body.childElementCount > 0 || doc.body.textContent?.trim() !== '';
  } catch {
    return true;
  }
}

async function waitForSrcdocIframesReady(surface: HTMLElement): Promise<void> {
  const iframes = Array.from(surface.querySelectorAll('iframe.a-html-iframe')) as HTMLIFrameElement[];
  if (iframes.length === 0) return;

  const start = performance.now();
  while (performance.now() - start < IFRAME_READY_TIMEOUT_MS) {
    if (iframes.every(isSrcdocIframeReady)) return;
    requestIframeMeasurements(surface);
    await wait(50);
    await waitForNextFrame();
  }
}

function requestIframeMeasurements(surface: HTMLElement): void {
  const iframes = surface.querySelectorAll('iframe.a-html-iframe');
  for (const node of Array.from(iframes)) {
    const iframe = node as HTMLIFrameElement;
    try {
      iframe.contentWindow?.postMessage({ __artifactMeasure: true }, '*');
    } catch {
      // Ignore inaccessible or detached frames.
    }
  }
}

function requestIframeWarmup(surface: HTMLElement): void {
  const iframes = surface.querySelectorAll('iframe.a-html-iframe');
  for (const node of Array.from(iframes)) {
    const iframe = node as HTMLIFrameElement;
    try {
      iframe.contentWindow?.postMessage({ __artifactWarmup: true }, '*');
    } catch {
      // Ignore inaccessible or detached frames.
    }
  }
}

function getSurfaceLayoutSignature(surface: HTMLElement): string {
  const { width, height } = getSurfaceSize(surface);
  const iframeSig = Array.from(surface.querySelectorAll('iframe.a-html-iframe'))
    .map((node) => {
      const iframe = node as HTMLIFrameElement;
      const rect = iframe.getBoundingClientRect();
      return `${Math.round(rect.width)}x${Math.round(rect.height)}:${iframe.style.height}`;
    })
    .join('|');
  return `${width}x${height}:${surface.scrollHeight}:${surface.childElementCount}:${iframeSig}`;
}

async function waitForStableSurfaceLayout(surface: HTMLElement): Promise<void> {
  await waitForSrcdocIframesReady(surface);
  await waitForImagesReady(surface);

  const start = performance.now();
  const maxWaitMs = 250;
  const stableWindowMs = 50;
  const sampleMs = 25;

  requestIframeWarmup(surface);
  requestIframeMeasurements(surface);
  await waitForAnimationFrame();
  await waitForAnimationFrame();

  let previous = getSurfaceLayoutSignature(surface);
  let stableSince = performance.now();

  while (performance.now() - start < maxWaitMs) {
    await wait(sampleMs);
    await waitForAnimationFrame();
    requestIframeMeasurements(surface);
    const current = getSurfaceLayoutSignature(surface);
    if (current !== previous) {
      previous = current;
      stableSince = performance.now();
      continue;
    }
    if (performance.now() - stableSince >= stableWindowMs) {
      return;
    }
  }
}

async function capturePngViaHtmlToImage(
  surface: HTMLElement,
  width: number,
  height: number,
  fullHeight: number,
  backgroundColor: string,
  imageMimeType: RasterImageMimeType,
  yStart = 0,
): Promise<string> {
  let pixelRatio = pickSafeOutputPixelRatio(width, height);

  for (let attempt = 0; attempt < 5; attempt += 1) {
    const tilePixelRatio = Math.min(pixelRatio, pickSafeTilePixelRatio(width));
    const tileHeight = Math.min(height, pickTileHeight(width, tilePixelRatio));

    const outputWidth = Math.max(1, Math.round(width * pixelRatio));
    const outputHeight = Math.max(1, Math.round(height * pixelRatio));

    try {
      const stitched = document.createElement('canvas');
      stitched.width = outputWidth;
      stitched.height = outputHeight;
      if (stitched.width !== outputWidth || stitched.height !== outputHeight) {
        throw new Error('Export canvas exceeds browser limits.');
      }
      const ctx = stitched.getContext('2d');
      if (!ctx) throw new Error('Failed to create export canvas.');

      for (let y = 0; y < height; y += tileHeight) {
        const sliceHeight = Math.min(tileHeight, height - y);
        const sliceCanvas = await toCanvas(surface, {
          cacheBust: true,
          skipFonts: true,
          skipAutoScale: true,
          pixelRatio: tilePixelRatio,
          width,
          height: sliceHeight,
          backgroundColor,
          style: {
            width: px(width),
            height: px(fullHeight),
            minHeight: '0px',
            backgroundColor,
            transform: `translateY(${px(-(y + yStart))})`,
            transformOrigin: 'top left',
          },
        });

        const destY = Math.round(y * pixelRatio);
        const destHeight = Math.round(sliceHeight * pixelRatio);
        ctx.drawImage(sliceCanvas, 0, destY, outputWidth, destHeight);
      }

      return encodeCanvasDataUrl(stitched, imageMimeType);
    } catch {
      pixelRatio *= 0.75;
      if (pixelRatio < 0.1) break;
    }
  }

  throw new Error('html-to-image render failed');
}

/**
 * Resolve a CSS color value that uses modern color syntax to an rgba/hex
 * string by drawing it onto a 1x1 canvas (the browser does the conversion).
 */
function resolveModernColor(value: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return value;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = '#000000';
  ctx.fillStyle = value;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return a < 255
    ? `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`
    : `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/**
 * Match modern CSS color functions unsupported by html2canvas, supporting
 * one level of nested parentheses (e.g. `oklch(calc(50%) 0.2 240)` or
 * `color-mix(in srgb, var(--accent) 55%, transparent)`).
 *
 * The inner group `[^()]*(?:\([^()]*\)[^()]*)*` matches:
 *   - plain tokens without parens, then
 *   - optionally one level of nested parens followed by more plain tokens.
 */
const MODERN_COLOR_RE = /\b(?:oklch|oklab|lab|lch|hwb|color|color-mix)\([^()]*(?:\([^()]*\)[^()]*)*\)/gi;

function containsModernColor(value: string): boolean {
  MODERN_COLOR_RE.lastIndex = 0;
  return MODERN_COLOR_RE.test(value);
}

function replaceModernColors(value: string): string {
  MODERN_COLOR_RE.lastIndex = 0;
  return value.replace(MODERN_COLOR_RE, (match) => resolveModernColor(match));
}

function normalizeModernColorValue(value: string): string {
  return containsModernColor(value) ? replaceModernColors(value) : value;
}

/** Color-related CSS properties that may contain modern color values. */
const COLOR_PROPERTIES = [
  'accent-color', 'caret-color', 'color', 'background', 'background-color', 'border-color',
  'border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color',
  'column-rule-color', 'outline-color', 'text-decoration-color', 'text-emphasis-color',
  'box-shadow', 'text-shadow',
  'fill', 'stroke', 'stop-color', 'flood-color', 'lighting-color',
];

/**
 * Replace modern color functions in all stylesheets, inline styles, AND computed
 * styles in the cloned document so html2canvas (which only supports
 * rgb/hsl) can parse them.
 */
function convertModernColorsInClone(doc: Document): void {
  // 1. Replace in <style> tags
  for (const style of Array.from(doc.querySelectorAll('style'))) {
    if (style.textContent) {
      style.textContent = replaceModernColors(style.textContent);
    }
  }

  // 2. Replace in inline styles
  for (const el of Array.from(doc.querySelectorAll('[style]')) as HTMLElement[]) {
    const attr = el.getAttribute('style');
    if (attr) {
      el.setAttribute('style', replaceModernColors(attr));
    }
  }

  // 3. Inline computed color properties that contain modern color functions.
  //    html2canvas reads computed styles directly — stylesheet replacements
  //    alone don't help because the browser may have already resolved modern
  //    stylesheet values into `color(...)` computed forms.
  for (const el of Array.from(doc.querySelectorAll('*')) as HTMLElement[]) {
    const cs = doc.defaultView?.getComputedStyle(el);
    if (!cs) continue;
    for (const prop of COLOR_PROPERTIES) {
      const val = cs.getPropertyValue(prop);
      if (val && containsModernColor(val)) {
        el.style.setProperty(prop, replaceModernColors(val));
      }
    }
  }
}

/**
 * Serialize all CSS rules from a document's styleSheets (including external
 * <link> sheets that the browser has parsed). Returns a single CSS string.
 */
function serializeAllCssRules(doc: Document): string {
  const chunks: string[] = [];
  for (const sheet of Array.from(doc.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) {
        chunks.push(rule.cssText);
      }
    } catch {
      // Cross-origin stylesheet — skip (can't read rules)
    }
  }
  return chunks.join('\n');
}

interface RasterizedIframe {
  index: number;
  dataUrl: string;
  width: number;
  height: number;
}

interface ExpandedSrcdocIframe {
  iframe: HTMLIFrameElement;
  height: string;
  overflow: string;
  scrolling: string | null;
}

interface AppliedIframeOverlay {
  img: HTMLImageElement;
  iframe: HTMLIFrameElement;
  previousDisplay: string;
}

function getSrcdocIframeContentSize(
  iframe: HTMLIFrameElement,
  doc = iframe.contentDocument,
): { width: number; height: number } {
  const rect = iframe.getBoundingClientRect();
  const root = doc?.documentElement;
  const body = doc?.body;
  const width = Math.max(
    1,
    Math.round(
      Math.max(
        rect.width,
        iframe.clientWidth,
        root?.scrollWidth ?? 0,
        root?.clientWidth ?? 0,
        body?.scrollWidth ?? 0,
        body?.clientWidth ?? 0,
      ),
    ),
  );
  const height = Math.max(
    1,
    Math.round(
      Math.max(
        rect.height,
        iframe.clientHeight,
        root?.scrollHeight ?? 0,
        root?.offsetHeight ?? 0,
        root?.clientHeight ?? 0,
        body?.scrollHeight ?? 0,
        body?.offsetHeight ?? 0,
        body?.clientHeight ?? 0,
      ),
    ),
  );
  return { width, height };
}

function dispatchIframeResize(iframe: HTMLIFrameElement): void {
  const win = iframe.contentWindow;
  if (!win) return;
  try {
    const event = iframe.contentDocument?.createEvent('Event') ?? new Event('resize');
    event.initEvent('resize', false, false);
    win.dispatchEvent(event);
  } catch {
    // Ignore detached or inaccessible frames.
  }
}

async function expandSrcdocIframesForExport(surface: HTMLElement): Promise<() => void> {
  await waitForSrcdocIframesReady(surface);

  const expanded: ExpandedSrcdocIframe[] = [];
  const iframes = Array.from(surface.querySelectorAll('iframe.a-html-iframe')) as HTMLIFrameElement[];

  for (const iframe of iframes) {
    let size: { width: number; height: number };
    try {
      size = getSrcdocIframeContentSize(iframe);
    } catch {
      continue;
    }

    const rect = iframe.getBoundingClientRect();
    if (size.height <= Math.ceil(rect.height) + 1) continue;

    expanded.push({
      iframe,
      height: iframe.style.height,
      overflow: iframe.style.overflow,
      scrolling: iframe.getAttribute('scrolling'),
    });
    iframe.style.height = px(size.height);
    iframe.style.overflow = 'hidden';
    iframe.setAttribute('scrolling', 'no');
    try {
      iframe.contentWindow?.scrollTo(0, 0);
    } catch {
      // Ignore detached or inaccessible frames.
    }
    dispatchIframeResize(iframe);
  }

  if (expanded.length > 0) {
    await waitForNextFrame();
    requestIframeMeasurements(surface);
    await waitForNextFrame();
  }

  return () => {
    for (const item of expanded) {
      item.iframe.style.height = item.height;
      item.iframe.style.overflow = item.overflow;
      if (item.scrolling === null) item.iframe.removeAttribute('scrolling');
      else item.iframe.setAttribute('scrolling', item.scrolling);
      dispatchIframeResize(item.iframe);
    }
  };
}

async function rasterizeSrcdocIframes(surface: HTMLElement): Promise<RasterizedIframe[]> {
  const snapshots: RasterizedIframe[] = [];
  const iframes = Array.from(surface.querySelectorAll('iframe.a-html-iframe'));

  for (let i = 0; i < iframes.length; i += 1) {
    const iframe = iframes[i] as HTMLIFrameElement;
    let doc: Document | null = null;
    try {
      doc = iframe.contentDocument;
    } catch {
      continue;
    }
    if (!doc?.documentElement || !doc.body) continue;
    const iframeDoc = doc;

    const { width, height } = getSrcdocIframeContentSize(iframe, iframeDoc);

    try {
      const canvas = await html2canvas(iframeDoc.documentElement as HTMLElement, {
        backgroundColor: null,
        useCORS: true,
        logging: false,
        scale: Math.min(2, window.devicePixelRatio || 1),
        x: 0,
        y: 0,
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        onclone: (clonedDoc: Document) => {
          convertModernColorsInClone(clonedDoc);
          if (clonedDoc.body) copyCanvasPixels(iframeDoc.body, clonedDoc.body);
        },
      });
      snapshots.push({
        index: i,
        dataUrl: encodeCanvasDataUrl(canvas, 'image/png'),
        width,
        height,
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.warn('[artifact-export] iframe rasterization failed, falling back to DOM inlining:', msg);
    }
  }

  return snapshots;
}

function applyIframeSnapshotOverlays(
  surface: HTMLElement,
  snapshots: RasterizedIframe[],
): AppliedIframeOverlay[] {
  const applied: AppliedIframeOverlay[] = [];
  const iframes = Array.from(surface.querySelectorAll('iframe.a-html-iframe'));

  for (const snapshot of snapshots) {
    const iframe = iframes[snapshot.index] as HTMLIFrameElement | undefined;
    const parent = iframe?.parentElement;
    if (!iframe || !parent) continue;

    const img = document.createElement('img');
    img.className = 'a-html-iframe-rasterized';
    img.alt = iframe.title || 'Artifact HTML';
    img.src = snapshot.dataUrl;
    img.width = snapshot.width;
    img.height = snapshot.height;
    img.setAttribute(
      'style',
      `${iframe.getAttribute('style') ?? ''};` +
      `display:block;width:${px(snapshot.width)};height:${px(snapshot.height)};` +
      'object-fit:contain;',
    );

    const previousDisplay = iframe.style.display;
    iframe.style.display = 'none';
    parent.insertBefore(img, iframe);
    applied.push({ img, iframe, previousDisplay });
  }

  return applied;
}

function removeIframeSnapshotOverlays(applied: AppliedIframeOverlay[]): void {
  for (const { img, iframe, previousDisplay } of applied) {
    iframe.style.display = previousDisplay;
    img.remove();
  }
}

function replaceSrcdocIframesWithImages(
  clonedSurface: HTMLElement,
  snapshots: RasterizedIframe[],
): Set<number> {
  const replaced = new Set<number>();
  const cloneIframes = Array.from(clonedSurface.querySelectorAll('iframe.a-html-iframe'));

  for (const snapshot of snapshots) {
    const cloneIframe = cloneIframes[snapshot.index] as HTMLIFrameElement | undefined;
    if (!cloneIframe?.parentElement) continue;

    const img = cloneIframe.ownerDocument.createElement('img');
    img.className = 'a-html-iframe-rasterized';
    img.alt = cloneIframe.title || 'Artifact HTML';
    img.src = snapshot.dataUrl;
    img.width = snapshot.width;
    img.height = snapshot.height;
    img.setAttribute(
      'style',
      `${cloneIframe.getAttribute('style') ?? ''};` +
      `display:block;width:${px(snapshot.width)};height:${px(snapshot.height)};` +
      'object-fit:contain;',
    );
    cloneIframe.parentElement.replaceChild(img, cloneIframe);
    replaced.add(snapshot.index);
  }

  return replaced;
}

/**
 * Replace srcdoc iframes in the cloned DOM with inline divs containing their
 * rendered content. html2canvas can't traverse into iframes, but since
 * srcdoc iframes are same-origin we can read their contentDocument from the
 * original surface and inject the HTML into the clone.
 */
function inlineSrcdocIframes(
  surface: HTMLElement,
  clonedSurface: HTMLElement,
  skipIndexes = new Set<number>(),
): void {
  const origIframes = Array.from(surface.querySelectorAll('iframe.a-html-iframe'));
  const cloneIframes = Array.from(clonedSurface.querySelectorAll('iframe.a-html-iframe'));

  for (let i = 0; i < origIframes.length && i < cloneIframes.length; i++) {
    if (skipIndexes.has(i)) continue;
    const origIframe = origIframes[i] as HTMLIFrameElement;
    const cloneIframe = cloneIframes[i] as HTMLIFrameElement;

    let doc: Document;
    let bodyHtml: string;
    let allCss: string;
    let bodyStyles: string;
    try {
      doc = origIframe.contentDocument!;
      if (!doc?.body) continue;

      bodyHtml = doc.body.innerHTML;

      // Serialize ALL CSS rules from the iframe's document — this includes
      // rules from external <link> stylesheets and inline <style> tags.
      allCss = serializeAllCssRules(doc);

      // Collect computed styles from the iframe's body for the wrapper
      const cs = doc.defaultView?.getComputedStyle(doc.body);
      bodyStyles = cs
        ? `color:${cs.color};background:${cs.backgroundColor};font-family:${cs.fontFamily};font-size:${cs.fontSize};line-height:${cs.lineHeight};`
        : '';
    } catch {
      // Cross-origin or inaccessible — skip
      continue;
    }

    const wrapper = cloneIframe.ownerDocument.createElement('div');
    wrapper.className = 'a-html-iframe-inlined';
    wrapper.setAttribute(
      'style',
      (cloneIframe.getAttribute('style') ?? '') +
      `;overflow:hidden;${bodyStyles}`,
    );
    // Inject the collected CSS and body HTML
    wrapper.innerHTML = (allCss ? `<style>${allCss}</style>` : '') + bodyHtml;

    // Copy pixel data from live canvases in the original iframe into the
    // (empty) canvas elements that innerHTML created in the wrapper.
    // innerHTML serializes <canvas> tags but not their pixel content, and
    // JS-set .width/.height properties are also lost (defaulting to 300×150).
    // We restore both so html2canvas can render them natively via drawImage.
    copyCanvasPixels(doc.body, wrapper);

    cloneIframe.parentElement?.replaceChild(wrapper, cloneIframe);
  }
}

/**
 * Copy pixel data from live canvas elements in ``src`` into the
 * corresponding (empty) canvas elements in ``dest``, matched by DOM order.
 *
 * Also restores the buffer dimensions (``canvas.width`` / ``canvas.height``)
 * which are lost during innerHTML serialization (they are JS properties,
 * not HTML attributes, so they default to 300×150 in the parsed copy).
 *
 * Does not mutate ``src`` — the original iframe DOM stays intact.
 */
function copyCanvasPixels(src: HTMLElement, dest: HTMLElement): void {
  const srcCanvases = Array.from(src.querySelectorAll('canvas')) as HTMLCanvasElement[];
  const destCanvases = Array.from(dest.querySelectorAll('canvas')) as HTMLCanvasElement[];

  for (let i = 0; i < srcCanvases.length && i < destCanvases.length; i++) {
    const orig = srcCanvases[i];
    const copy = destCanvases[i];
    // Restore buffer dimensions (lost by innerHTML round-trip)
    copy.width = orig.width;
    copy.height = orig.height;
    try {
      const ctx = copy.getContext('2d');
      if (ctx) ctx.drawImage(orig, 0, 0);
    } catch {
      // Tainted canvas (cross-origin images drawn on it) — skip
    }
  }
}

async function capturePngViaHtml2Canvas(
  surface: HTMLElement,
  height: number,
  backgroundColor: string,
  imageMimeType: RasterImageMimeType,
  iframeSnapshots: RasterizedIframe[],
  yStart = 0,
): Promise<string> {
  const canvas = await html2canvas(surface, {
    backgroundColor,
    y: yStart,
    height,
    useCORS: true,
    logging: false,
    scale: Math.min(2, window.devicePixelRatio || 1),
    onclone: (doc: Document, clonedEl: HTMLElement) => {
      const replacedIndexes = replaceSrcdocIframesWithImages(clonedEl, iframeSnapshots);
      inlineSrcdocIframes(surface, clonedEl, replacedIndexes);
      convertModernColorsInClone(doc);
    },
  });
  return encodeCanvasDataUrl(canvas, imageMimeType);
}

async function capturePngDataUrl(
  surface: HTMLElement,
  maxHeight?: number,
  offsetY?: number,
  imageMimeType: RasterImageMimeType = 'image/png',
): Promise<string> {
  const size = getSurfaceSize(surface);
  const width = size.width;
  const yStart = Math.max(0, Math.min(offsetY ?? 0, size.height));
  const availableHeight = size.height - yStart;
  const height = maxHeight ? Math.min(availableHeight, maxHeight) : availableHeight;
  const backgroundColor = resolveCaptureBackgroundColor(surface);
  const errors: string[] = [];

  // html-to-image uses SVG foreignObject which cannot traverse into iframes,
  // so surfaces containing srcdoc iframes must go through html2canvas after
  // replacing each iframe with a same-origin raster snapshot.
  const hasSrcdocIframes = surface.querySelector('iframe.a-html-iframe') !== null;
  const preferHtml2Canvas = hasInlineImageSources(surface);

  if (hasSrcdocIframes) {
    const iframeSnapshots = await rasterizeSrcdocIframes(surface);
    const tryHtml2Canvas = async () => {
      try {
        return await capturePngViaHtml2Canvas(
          surface,
          height,
          backgroundColor,
          imageMimeType,
          iframeSnapshots,
          yStart,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errors.push(`html2canvas: ${msg}`);
        console.warn('[artifact-export] html2canvas failed:', msg);
        return null;
      }
    };
    const tryHtmlToImage = async () => {
      const overlays = applyIframeSnapshotOverlays(surface, iframeSnapshots);
      try {
        return await capturePngViaHtmlToImage(
          surface,
          width,
          height,
          size.height,
          backgroundColor,
          imageMimeType,
          yStart,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errors.push(`html-to-image with iframe snapshots: ${msg}`);
        console.warn('[artifact-export] html-to-image with iframe snapshots failed:', msg);
        return null;
      } finally {
        removeIframeSnapshotOverlays(overlays);
      }
    };

    if (preferHtml2Canvas || iframeSnapshots.length > 0) {
      const html2canvasResult = await tryHtml2Canvas();
      if (html2canvasResult) return html2canvasResult;
      const htmlToImageResult = await tryHtmlToImage();
      if (htmlToImageResult) return htmlToImageResult;
    } else {
      const htmlToImageResult = await tryHtmlToImage();
      if (htmlToImageResult) return htmlToImageResult;
      const html2canvasResult = await tryHtml2Canvas();
      if (html2canvasResult) return html2canvasResult;
    }
  } else {
    const tryHtmlToImage = async () => {
      try {
        return await capturePngViaHtmlToImage(
          surface,
          width,
          height,
          size.height,
          backgroundColor,
          imageMimeType,
          yStart,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errors.push(`html-to-image: ${msg}`);
        console.warn('[artifact-export] html-to-image failed:', msg);
        return null;
      }
    };
    const tryHtml2Canvas = async () => {
      try {
        return await capturePngViaHtml2Canvas(
          surface,
          height,
          backgroundColor,
          imageMimeType,
          [],
          yStart,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        errors.push(`html2canvas: ${msg}`);
        console.warn('[artifact-export] html2canvas failed:', msg);
        return null;
      }
    };

    // html-to-image is usually higher quality, but it can silently omit
    // inline data-URL image pixels while still returning a canvas. For those
    // surfaces, use html2canvas first because it paints loaded images via
    // drawImage.
    if (preferHtml2Canvas) {
      const html2canvasResult = await tryHtml2Canvas();
      if (html2canvasResult) return html2canvasResult;
      const htmlToImageResult = await tryHtmlToImage();
      if (htmlToImageResult) return htmlToImageResult;
    } else {
      const htmlToImageResult = await tryHtmlToImage();
      if (htmlToImageResult) return htmlToImageResult;
      const html2canvasResult = await tryHtml2Canvas();
      if (html2canvasResult) return html2canvasResult;
    }
  }

  throw new Error(`Artifact PNG export failed (surface ${width}x${size.height}, capture height ${height}, offsetY ${yStart}). ${errors.join('; ')}`);
}

async function captureSurfaceSlice(
  surface: HTMLElement,
  opts: {
    width: number;
    height: number;
    y: number;
    sliceHeight: number;
    pixelRatio: number;
    backgroundColor: string;
  },
): Promise<HTMLCanvasElement> {
  const {
    width,
    height,
    y,
    sliceHeight,
    pixelRatio,
    backgroundColor,
  } = opts;
  return toCanvas(surface, {
    cacheBust: true,
    skipAutoScale: true,
    pixelRatio,
    width,
    height: sliceHeight,
    backgroundColor,
    style: {
      width: px(width),
      height: px(height),
      minHeight: px(0),
      backgroundColor,
      transform: `translateY(${px(-y)})`,
      transformOrigin: 'top left',
    },
  });
}

function dataUrlBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function readExportStyles(): string {
  return serializeAllCssRules(document);
}

function readArtifactThemeVars(surface: HTMLElement): string {
  const computed = getComputedStyle(surface);
  const vars = [
    '--artifact-primary',
    '--artifact-background',
    '--artifact-foreground',
    '--artifact-muted',
    '--artifact-border-radius',
    '--artifact-font',
    '--artifact-font-headline',
    '--artifact-font-mono',
    '--artifact-gap',
    '--artifact-shadow',
  ];
  return vars
    .map((name) => `${name}:${computed.getPropertyValue(name).trim()};`)
    .join('');
}

function buildSnapshotHtml(title: string, surfaceHtml: string, cssText: string, themeVars: string): string {
  const safeTitle = escapeHtml(title);
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${safeTitle}</title>
    <style>
      body {
        margin: 0;
        padding: 24px;
        background: #f3f4f6;
        font-family: system-ui, -apple-system, Segoe UI, sans-serif;
      }
      .snapshot {
        max-width: 1600px;
        margin: 0 auto;
        background: var(--artifact-background);
        border-radius: 12px;
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
        overflow: visible;
      }
      #artifact-root {
        ${themeVars}
      }
      #artifact-root .a-surface {
        min-height: auto !important;
        height: auto !important;
      }
    </style>
    <style>${cssText}</style>
  </head>
  <body>
    <div class="snapshot" id="artifact-root">
      ${surfaceHtml}
    </div>
  </body>
</html>`;
}

/**
 * Resolve the DOM element to capture for an export request.
 *
 * When ``componentId`` is provided, look for the ``.a-node`` wrapper
 * RenderNode emits for that cuid inside ``surface``. Falls back to
 * ``surface`` itself when the component can't be found (e.g. it was
 * unmounted between the request and the response).
 */
export function resolveCaptureTarget(
  surface: HTMLElement,
  componentId?: number | null,
): HTMLElement {
  if (componentId == null) return surface;
  const scoped = surface.querySelector(
    `.a-node[data-id="${String(componentId)}"]`,
  );
  if (scoped instanceof HTMLElement) return scoped;
  return surface;
}

export async function waitForCaptureTarget(
  getSurface: () => HTMLElement | null,
  componentId?: number | null,
  timeoutMs = 2000,
): Promise<CaptureTargetWaitResult> {
  const deadline = performance.now() + timeoutMs;
  let lastSurface: HTMLElement | null = null;

  while (performance.now() <= deadline) {
    const surface = getSurface();
    lastSurface = surface;
    if (surface) {
      const target = resolveCaptureTarget(surface, componentId);
      if (componentId == null || target !== surface) {
        return { ok: true, target };
      }
    }
    await waitForNextFrame();
  }

  if (!lastSurface) {
    return { ok: false, error: 'Artifact surface not available' };
  }
  return { ok: false, error: `Component #${componentId} not found on surface` };
}

export async function exportSurfaceSnapshot(
  surface: HTMLElement,
  format: ArtifactExportFormat,
  title = 'Artifact Snapshot',
  maxHeight?: number,
  offsetY?: number,
  options: ExportSurfaceSnapshotOptions = {},
): Promise<ArtifactExportPayload> {
  if (format === 'html') {
    const cssText = readExportStyles();
    const themeVars = readArtifactThemeVars(surface);
    const html = buildSnapshotHtml(title, surface.outerHTML, cssText, themeVars);
    return {
      format,
      mimeType: 'text/html',
      base64: textToBase64(html),
    };
  }

  const restoreIframes = await expandSrcdocIframesForExport(surface);
  if (format === 'png') {
    try {
      await waitForStableSurfaceLayout(surface);
      const pngDataUrl = await capturePngDataUrl(
        surface,
        maxHeight,
        offsetY,
        options.imageMimeType ?? 'image/png',
      );
      return {
        format,
        mimeType: dataUrlMimeType(pngDataUrl),
        base64: dataUrlBase64(pngDataUrl),
      };
    } finally {
      restoreIframes();
    }
  }

  try {
    await waitForStableSurfaceLayout(surface);
    const { width, height } = getSurfaceSize(surface);
    const backgroundColor = resolveCaptureBackgroundColor(surface);
    const tilePixelRatio = pickSafeTilePixelRatio(width);
    const maxTileHeight = pickTileHeight(width, tilePixelRatio);
    const pdf = new jsPDF({
      orientation: width >= height ? 'landscape' : 'portrait',
      unit: 'px',
      format: 'a4',
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const cssHeightPerPdfPage = Math.max(1, Math.floor((pageHeight * width) / pageWidth));

    let firstPage = true;
    for (let y = 0; y < height;) {
      const sliceHeight = Math.min(cssHeightPerPdfPage, maxTileHeight, height - y);
      const sliceCanvas = await captureSurfaceSlice(surface, {
        width,
        height,
        y,
        sliceHeight,
        pixelRatio: tilePixelRatio,
        backgroundColor,
      });
      const renderedSliceHeight = (sliceHeight * pageWidth) / width;
      if (!firstPage) {
        pdf.addPage();
      }
      pdf.addImage(sliceCanvas, 'PNG', 0, 0, pageWidth, renderedSliceHeight, undefined, 'FAST');
      firstPage = false;
      y += sliceHeight;
    }
    const bytes = new Uint8Array(pdf.output('arraybuffer') as ArrayBuffer);
    return {
      format,
      mimeType: 'application/pdf',
      base64: uint8ToBase64(bytes),
    };
  } finally {
    restoreIframes();
  }
}

export function downloadExportPayload(payload: ArtifactExportPayload, filename: string): void {
  const href = `data:${payload.mimeType};base64,${payload.base64}`;
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
